import { ContainerQuery } from '@/components/container-query/ContainerQuery';
import { CustomLink } from '@/components/custom-link/CustomLink';
import { useGenerateSpecialFileLink } from '@/hooks/useGenerateSpecialFileLink';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import { useUrlConfigModelStore } from '@/models/url-config/urlConfig';
import { DOCS_SITE_EVENT } from '@/shared/constant/Analytics';
import { SupportedLocale } from '@/shared/i18n/SupportedLocale';
import { gaReport } from '@/shared/util/analytics';
import { Button, Tooltip } from 'aui';
import { FoxIconActionsExport, FoxIconClone } from 'aui-biz-icons';
import clsx from 'clsx';
import { useState } from 'react';
import ExportModal from '../export-modal/ExportModal';
import { FooterSlogan } from '../footer-slogan/FooterSlogan';
import { useCloneProject } from './hooks/useCloneProject';
import { useGetResourceUtmCampaign } from './hooks/useGetResourceUtmCampaign';

export type FooterProps = {
  className?: string;
};

export function Footer(props: FooterProps) {
  const { className } = props;

  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);

  const enableLLMs = useDocsDataModelStore((x) => x.docsBaseConfig.enableLLMs);
  const language = useDocsDataModelStore((x) => x.docsBaseConfig.language);
  const isJP = language === SupportedLocale.JA_JP;
  const hideCloneButton = useDocsDataModelStore((x) => x.docsBaseConfig.hideCloneButton);
  const exportable = useDocsDataModelStore((x) => x.docsBaseConfig.exportable);
  const hideBuiltWith = useDocsDataModelStore((x) => x.docsBaseConfig.hideBuiltWith);
  const appName = useEnvConfigModelStore((x) => x.APP_NAME);
  const homeIndexUrl = useUrlConfigModelStore((x) => x.home.index);
  const title = useDocsDataModelStore((x) => x.docsBaseConfig.title);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const { isCloning, cloneProject } = useCloneProject();
  const onlineType = useDocsDataModelStore((x) => x.docsIdTypeData.onlineType);
  const utmCampaign = useGetResourceUtmCampaign();

  const { llmsSpecialFileLink } = useGenerateSpecialFileLink();
  const hasLLMsButton = enableLLMs && llmsSpecialFileLink;
  const hasCloneButton = exportable && !hideCloneButton;
  const hasExportButton = exportable;
  const hasButtons = hasLLMsButton || hasCloneButton || hasExportButton;

  return (
    <ContainerQuery containerSizes={[390, 460]} className="@container/footer">
      <footer
        className={clsx(
          'flex flex-col items-center gap-2 border-t border-t-color-split py-10',
          {
            ['@[390px]/footer:flex-row']: !isJP,
            ['@[460px]/footer:flex-row']: isJP,
          },
          className,
        )}
      >
        <div
          className={clsx('flex flex-wrap items-center justify-center text-base font-500', {
            ['@[390px]/footer:justify-start']: !isJP,
            ['@[460px]/footer:justify-start']: isJP,
          })}
        >
          {hasLLMsButton && (
            <CustomLink to={llmsSpecialFileLink} target="_blank">
              <Button type="text" className="px-3 text-secondary">
                {clientI18n('specialFile.menu.llms.label')}
              </Button>
            </CustomLink>
          )}

          {hasCloneButton && (
            <>
              <Tooltip
                align={{ offset: [0, 5] }}
                placement="top"
                title={clientI18n('onlineShare.clone', { appName })}
              >
                <Button
                  type="text"
                  icon={<FoxIconClone size={16} className="mr-1" />}
                  loading={isCloning}
                  className="px-3 text-secondary max-os:hidden"
                  onClick={() => {
                    gaReport(DOCS_SITE_EVENT.BUTTON_CLICK_CLONE_DATA, { page_title: title });
                    cloneProject();
                  }}
                >
                  {clientI18n('clone')}
                </Button>
              </Tooltip>
            </>
          )}
          {hasExportButton && (
            <Tooltip
              align={{ offset: [0, 5] }}
              placement="top"
              title={clientI18n('onlineShare.export')}
            >
              <Button
                type="text"
                icon={<FoxIconActionsExport size={16} className="mr-1" />}
                className="px-3 text-secondary max-os:hidden"
                onClick={() => setExportModalVisible(true)}
              >
                {clientI18n('export')}
              </Button>
            </Tooltip>
          )}
        </div>
        <div
          className={clsx('flex-1 flex-shrink-0', {
            ['text-right']: hasButtons,
            ['text-center']: !hasButtons,
            ['max-os:text-right']: hasLLMsButton,
            ['max-os:text-center']: !hasLLMsButton,
          })}
        >
          {!hideBuiltWith && (
            <FooterSlogan
              homeIndexUrl={homeIndexUrl}
              title={title}
              utmCampaign={utmCampaign}
              onlineType={onlineType}
            />
          )}
        </div>
      </footer>
      <ExportModal visible={exportModalVisible} onCancel={() => setExportModalVisible(false)} />
    </ContainerQuery>
  );
}
